import { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Save } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { getLitePersonnel, saveLitePersonnel } from '@/services/db/dsrEntry';

interface PersonnelLiteFormProps {
  dsrId: string;
  isReadOnly: boolean;
}

// Mirrors the DB CHECK constraints on dsr_reports:
//   chk_lite_pob_nonneg / chk_lite_sse_nonneg / chk_lite_sse_le_pob /
//   chk_lite_crew_note_len. Form-side validation gives friendlier messages.
const schema = z
  .object({
    total_pob: z.number().int('Whole number required').min(0, 'Cannot be negative').max(1000, 'Out of range'),
    sse_count: z.number().int('Whole number required').min(0, 'Cannot be negative').max(1000, 'Out of range'),
    crew_note: z.string().max(300, '300 character maximum').optional(),
  })
  .refine((d) => d.sse_count <= d.total_pob, {
    message: 'SSE count cannot exceed total POB',
    path: ['sse_count'],
  });

type FormValues = z.infer<typeof schema>;

export default function PersonnelLiteForm({ dsrId, isReadOnly }: PersonnelLiteFormProps) {
  const queryClient = useQueryClient();

  const { data: existing, isLoading } = useQuery({
    queryKey: ['lite_personnel', dsrId],
    queryFn: () => getLitePersonnel(dsrId),
    staleTime: 30_000,
  });

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isDirty },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { total_pob: 0, sse_count: 0, crew_note: '' },
  });

  useEffect(() => {
    if (existing) {
      reset({
        total_pob: existing.lite_total_pob ?? 0,
        sse_count: existing.lite_sse_count ?? 0,
        crew_note: existing.lite_crew_note ?? '',
      });
    }
  }, [existing, reset]);

  const { mutate: save, isPending: isSaving } = useMutation({
    mutationFn: (values: FormValues) =>
      saveLitePersonnel({
        dsrReportId: dsrId,
        totalPob: values.total_pob,
        sseCount: values.sse_count,
        crewNote: values.crew_note?.trim() ? values.crew_note.trim() : null,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lite_personnel', dsrId] });
      queryClient.invalidateQueries({ queryKey: ['entry_completion', dsrId] });
    },
    onError: (err: Error) => {
      alert(err.message || 'Could not save Personnel On Board');
    },
  });

  const onSave = handleSubmit((values) => save(values));
  const noteValue = watch('crew_note') ?? '';

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-10">
          <Loader2 className="h-5 w-5 animate-spin text-mozaic-teal" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Personnel On Board</CardTitle>
        <CardDescription>
          Total POB and SSE count for this shift. Per-crew detail is captured on the full
          Personnel section in Standard reports — Lite uses these totals only.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <Label htmlFor="lite_pob">Total POB *</Label>
            <Controller
              control={control}
              name="total_pob"
              render={({ field }) => (
                <Input
                  id="lite_pob"
                  type="number"
                  inputMode="numeric"
                  min={0}
                  max={1000}
                  disabled={isReadOnly}
                  value={Number.isFinite(field.value) ? field.value : ''}
                  onChange={(e) => {
                    const v = e.target.value;
                    const n = parseInt(v, 10);
                    field.onChange(Number.isFinite(n) ? n : 0);
                  }}
                />
              )}
            />
            {errors.total_pob && (
              <p className="mt-1 text-xs text-destructive">{errors.total_pob.message}</p>
            )}
          </div>

          <div>
            <Label htmlFor="lite_sse">SSE Count *</Label>
            <Controller
              control={control}
              name="sse_count"
              render={({ field }) => (
                <Input
                  id="lite_sse"
                  type="number"
                  inputMode="numeric"
                  min={0}
                  max={1000}
                  disabled={isReadOnly}
                  value={Number.isFinite(field.value) ? field.value : ''}
                  onChange={(e) => {
                    const v = e.target.value;
                    const n = parseInt(v, 10);
                    field.onChange(Number.isFinite(n) ? n : 0);
                  }}
                />
              )}
            />
            {errors.sse_count && (
              <p className="mt-1 text-xs text-destructive">{errors.sse_count.message}</p>
            )}
          </div>
        </div>

        <div>
          <Label htmlFor="lite_note">Crew Note</Label>
          <Controller
            control={control}
            name="crew_note"
            render={({ field }) => (
              <Textarea
                id="lite_note"
                rows={3}
                maxLength={300}
                disabled={isReadOnly}
                placeholder="Optional shift context (300 character max)"
                value={field.value ?? ''}
                onChange={field.onChange}
              />
            )}
          />
          <div className="mt-1 flex items-center justify-between">
            {errors.crew_note ? (
              <p className="text-xs text-destructive">{errors.crew_note.message}</p>
            ) : (
              <span />
            )}
            <p className="text-xs text-muted-foreground">{noteValue.length}/300</p>
          </div>
        </div>

        {!isReadOnly && (
          <div className="flex justify-end pt-2">
            <Button
              type="button"
              onClick={onSave}
              disabled={isSaving || !isDirty}
              className="bg-mozaic-teal text-white hover:bg-mozaic-teal/90"
            >
              {isSaving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <Save className="mr-1 h-4 w-4" />
                  Save
                </>
              )}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
